import Link from "next/link";
import localFont from "next/font/local";
import { type CSSProperties } from "react";

// The wordmark's serif, Martina Plantijn (Klim), licensed for
// tradewithtitans.com. See src/styles/fonts/files/README.md.
const brandSerif = localFont({
  src: [
    {
      path: "../styles/fonts/files/martina-plantijn-vf-roman.woff2",
      weight: "300 900",
      style: "normal",
    },
    {
      path: "../styles/fonts/files/martina-plantijn-vf-italic.woff2",
      weight: "300 900",
      style: "italic",
    },
  ],
  display: "swap",
  fallback: ["Georgia", "serif"],
  variable: "--twt-brand-serif",
});

const brandFontSettings = {} satisfies CSSProperties;

// The logo's own class merge. A class the caller passes replaces the default
// class from the same group (text size, text color, height, width, gap,
// radius, font weight), the one job tailwind-merge did here. The logo shows on
// every page, and tailwind-merge alone weighed more than the rest of it.
const groupOf = (token: string): string | null => {
  const base = token.slice(token.lastIndexOf(":") + 1);
  if (/^text-\[(#|rgb|var\(--)/.test(base) || /^text-(white|black|transparent|inherit|current)$/.test(base)) return "text-color";
  if (/^text-(\[\d|xs|sm|base|lg|[2-9]?xl)/.test(base)) return "text-size";
  const match = /^(h|w|gap|rounded|font|bg|tracking|leading)(-|$)/.exec(base);
  if (!match) return null;
  if (match[1] === "font" && !/^font-(thin|light|normal|medium|semibold|bold|extrabold|black)$/.test(base)) return null;
  return match[1]!;
};
const variant = (token: string) => token.slice(0, token.lastIndexOf(":") + 1);
function cn(...parts: (string | false | null | undefined)[]) {
  const tokens = parts.filter(Boolean).join(" ").split(/\s+/).filter(Boolean);
  const kept: string[] = [];
  tokens.forEach((token, index) => {
    const group = groupOf(token);
    // A later text size also replaces a line height, as in Tailwind.
    const replaced =
      group !== null &&
      tokens
        .slice(index + 1)
        .some(
          (later) =>
            variant(later) === variant(token) &&
            (groupOf(later) === group || (group === "leading" && groupOf(later) === "text-size")),
        );
    if (!replaced) kept.push(token);
  });
  return kept.join(" ");
}

const markSizes = {
  sm: "h-6 w-6 rounded-[6px] text-[13px]",
  md: "h-7 w-7 rounded-[7px] text-[15px]",
  lg: "h-9 w-9 rounded-[8px] text-[21px]",
};

const textSizes = {
  sm: "text-[18px]",
  md: "text-[20px]",
  lg: "text-[24px]",
};

type BrandSize = keyof typeof markSizes;

type TwtBrandMarkProps = {
  className?: string;
  size?: BrandSize;
  ariaHidden?: boolean;
  inverted?: boolean;
  style?: CSSProperties;
};

export function TwtBrandMark({
  className,
  size = "md",
  ariaHidden = true,
  inverted = false,
  style,
}: TwtBrandMarkProps) {
  return (
    <span
      aria-hidden={ariaHidden}
      className={cn(
        brandSerif.variable,
        "inline-flex flex-none items-center justify-center font-medium leading-none [font-family:var(--twt-brand-serif),Georgia,serif]",
        inverted
          ? "bg-[#faf9f5] text-[#141413]"
          : "bg-[#141413] text-[#faf9f5]",
        markSizes[size],
        className
      )}
      style={{ ...brandFontSettings, ...style }}
    >
      T
    </span>
  );
}

type TwtBrandWordProps = {
  className?: string;
  emphasisClassName?: string;
  size?: BrandSize;
  /**
   * Draws the words with CSS generated content, hidden from assistive
   * technology, inside role="img", so the wordmark is one logo image named
   * "Trade with Titans" and has no text nodes.
   * WCAG 1.4.3 exempts logotypes from contrast, and the clay "with" is 2.96:1.
   */
  logo?: boolean;
};

// The rule sits next to the words so they draw on first paint.
const LOGO_WORD_CSS = ".twt-logo-word [data-word]::before{content:attr(data-word)}";

export function TwtBrandWord({
  className,
  emphasisClassName,
  size = "md",
  logo = false,
}: TwtBrandWordProps) {
  if (logo)
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: LOGO_WORD_CSS }} />
        <span
          role="img"
          aria-label="Trade with Titans"
          className={cn(
            brandSerif.variable,
            "twt-logo-word font-medium leading-none tracking-normal text-[#141413] [font-family:var(--twt-brand-serif),Georgia,serif]",
            textSizes[size],
            className
          )}
          style={brandFontSettings}
        >
          <span aria-hidden="true">
            <span data-word="Trade" />{" "}
            <em
              data-word="with"
              className={cn("font-normal italic text-[#d97757]", emphasisClassName)}
            />{" "}
            <span data-word="Titans" />
          </span>
        </span>
      </>
    );
  return (
    <span
      className={cn(
        // The brand wordmark must never truncate or ellipsize — the full
        // "Trade with Titans" has to render at every width. We let it wrap
        // rather than clip if it ever runs out of room.
        brandSerif.variable,
        "font-medium leading-none tracking-normal text-[#141413] [font-family:var(--twt-brand-serif),Georgia,serif]",
        textSizes[size],
        className
      )}
      style={brandFontSettings}
    >
      Trade{" "}
      <em
        className={cn("font-normal italic text-[#d97757]", emphasisClassName)}
      >
        with
      </em>{" "}
      Titans
    </span>
  );
}

type TwtBrandProps = {
  href?: string;
  className?: string;
  markClassName?: string;
  textClassName?: string;
  emphasisClassName?: string;
  size?: BrandSize;
  invertedMark?: boolean;
  logo?: boolean;
};

export function TwtBrand({
  href,
  className,
  markClassName,
  textClassName,
  emphasisClassName,
  size = "md",
  invertedMark = false,
  logo = false,
}: TwtBrandProps) {
  const content = (
    <>
      <TwtBrandMark
        className={markClassName}
        size={size}
        inverted={invertedMark}
      />
      <TwtBrandWord
        className={textClassName}
        emphasisClassName={emphasisClassName}
        size={size}
        logo={logo}
      />
    </>
  );
  const sharedClassName = cn("inline-flex items-center gap-2.5", className);

  if (href) {
    return (
      <Link
        href={href}
        aria-label="Trade with Titans home"
        className={sharedClassName}
      >
        {content}
      </Link>
    );
  }

  return <span className={sharedClassName}>{content}</span>;
}
